import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import { AsyncState, asResult, computedFailure, initialState, loadingState } from '@libs/core/lib/state/async-states';
import {
  addAddress,
  addFcmToken,
  addToWishlist,
  deleteAddress,
  loggedInUser,
  removeFcmToken,
  removeFromWishlist,
  updateAddress,
} from '@libs/mtjr/shared/generated/entities/user/service/user.service';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withAddToWishlist() {
  return signalStoreFeature(
    withState<{ addToWishlistState: AsyncState<void> }>({
      addToWishlistState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async addToWishlist({ productId }: Dtos.AddToWishlistPathParams) {
        patchState(store, { addToWishlistState: loadingState() });
        const result = await asResult(addToWishlist(apiService, { productId }));
        patchState(store, { addToWishlistState: result });
      },
    })),
    withComputed((store) => ({
      addToWishlistLoading: computed(() => store.addToWishlistState().type === 'loading'),
      addToWishlistFailure: computedFailure(store.addToWishlistState),
    })),
  );
}

export function withRemoveFromWishlist() {
  return signalStoreFeature(
    withState<{ removeFromWishlistState: AsyncState<void> }>({
      removeFromWishlistState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async removeFromWishlist({ productId }: Dtos.RemoveFromWishlistPathParams) {
        patchState(store, { removeFromWishlistState: loadingState() });
        const result = await asResult(removeFromWishlist(apiService, { productId }));
        patchState(store, { removeFromWishlistState: result });
      },
    })),
    withComputed((store) => ({
      removeFromWishlistLoading: computed(() => store.removeFromWishlistState().type === 'loading'),
      removeFromWishlistFailure: computedFailure(store.removeFromWishlistState),
    })),
  );
}

export function withAddAddress() {
  return signalStoreFeature(
    withState<{ addAddressState: AsyncState<void> }>({
      addAddressState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async addAddress(addAddressBody: Dtos.AddAddressBody) {
        patchState(store, { addAddressState: loadingState() });
        const result = await asResult(addAddress(apiService, addAddressBody));
        patchState(store, { addAddressState: result });
      },
    })),
    withComputed((store) => ({
      addAddressLoading: computed(() => store.addAddressState().type === 'loading'),
      addAddressFailure: computedFailure(store.addAddressState),
    })),
  );
}

export function withUpdateAddress() {
  return signalStoreFeature(
    withState<{ updateAddressState: AsyncState<void> }>({
      updateAddressState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async updateAddress(updateAddressBody: Dtos.UpdateAddressBody, { addressId }: Dtos.UpdateAddressPathParams) {
        patchState(store, { updateAddressState: loadingState() });
        const result = await asResult(updateAddress(apiService, updateAddressBody, { addressId }));
        patchState(store, { updateAddressState: result });
      },
    })),
    withComputed((store) => ({
      updateAddressLoading: computed(() => store.updateAddressState().type === 'loading'),
      updateAddressFailure: computedFailure(store.updateAddressState),
    })),
  );
}

export function withDeleteAddress() {
  return signalStoreFeature(
    withState<{ deleteAddressState: AsyncState<void> }>({
      deleteAddressState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async deleteAddress({ addressId }: Dtos.DeleteAddressPathParams) {
        patchState(store, { deleteAddressState: loadingState() });
        const result = await asResult(deleteAddress(apiService, { addressId }));
        patchState(store, { deleteAddressState: result });
      },
    })),
    withComputed((store) => ({
      deleteAddressLoading: computed(() => store.deleteAddressState().type === 'loading'),
      deleteAddressFailure: computedFailure(store.deleteAddressState),
    })),
  );
}

export function withAddFCMToken() {
  return signalStoreFeature(
    withState<{ addFcmTokenState: AsyncState<void> }>({
      addFcmTokenState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async addFcmToken(addFcmTokenBody: Dtos.AddFCMTokenBody) {
        patchState(store, { addFcmTokenState: loadingState() });
        const result = await asResult(addFcmToken(apiService, addFcmTokenBody));
        patchState(store, { addFcmTokenState: result });
      },
    })),
    withComputed((store) => ({
      addFcmTokenLoading: computed(() => store.addFcmTokenState().type === 'loading'),
      addFcmTokenFailure: computedFailure(store.addFcmTokenState),
    })),
  );
}

export function withRemoveFCMToken() {
  return signalStoreFeature(
    withState<{ removeFcmTokenState: AsyncState<void> }>({
      removeFcmTokenState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async removeFcmToken(removeFcmTokenBody: Dtos.RemoveFCMTokenBody) {
        patchState(store, { removeFcmTokenState: loadingState() });
        const result = await asResult(removeFcmToken(apiService, removeFcmTokenBody));
        patchState(store, { removeFcmTokenState: result });
      },
    })),
    withComputed((store) => ({
      removeFcmTokenLoading: computed(() => store.removeFcmTokenState().type === 'loading'),
      removeFcmTokenFailure: computedFailure(store.removeFcmTokenState),
    })),
  );
}

export function withLoggedInUser() {
  return signalStoreFeature(
    withState<{ loggedInUserState: AsyncState<Entity.User> }>({
      loggedInUserState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async loggedInUser() {
        patchState(store, { loggedInUserState: loadingState() });
        const result = await asResult(loggedInUser(apiService));
        patchState(store, { loggedInUserState: result });
      },
    })),
    withComputed((store) => ({
      loggedInUserLoading: computed(() => store.loggedInUserState().type === 'loading'),
      loggedInUserFailure: computedFailure(store.loggedInUserState),
    })),
  );
}
